import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, MapPin, Calendar, BarChart3, FileText, Database, LogOut, Users, FileBarChart, GitCompare, Menu, X, ShoppingCart, Package, Archive, TrendingUp, Zap, FileEdit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const baseNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Convention Manager', href: '/events', icon: MapPin },
  { name: 'Calendar & Tasks', href: '/calendar', icon: Calendar },
  { name: 'Execution Board', href: '/execution-board', icon: Zap },
  { name: 'Meeting Hub', href: '/meeting-notes', icon: FileEdit },
  { name: 'Documents', href: '/documents', icon: FileText },
  { name: 'Find Conventions', href: '/convention-database', icon: Database },
];

const managementNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Convention Manager', href: '/events', icon: MapPin },
  { name: 'Calendar & Tasks', href: '/calendar', icon: Calendar },
  { name: 'Execution Board', href: '/execution-board', icon: Zap },
  { name: 'Meeting Hub', href: '/meeting-notes', icon: FileEdit },
  { name: 'Sales Accounting', href: '/product-sales', icon: Package },
  { name: 'Inventory', href: '/inventory', icon: Archive },
  { name: 'Convention Sales', href: '/convention-sales', icon: ShoppingCart },
  { name: 'Staff & Finance', href: '/staff-hub', icon: Users },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Show Profitability', href: '/show-profitability', icon: TrendingUp },
  { name: 'Reports', href: '/reports', icon: FileBarChart },
  { name: 'Documents', href: '/documents', icon: FileText },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Find Conventions', href: '/convention-database', icon: Database },
];

// Mobile bottom nav - only show 4 key items
const mobileNavigation = [
  { name: 'Home', href: '/', icon: LayoutDashboard },
  { name: 'Conventions', href: '/events', icon: MapPin },
  { name: 'Calendar', href: '/calendar', icon: Calendar },
  { name: 'Execute', href: '/execution-board', icon: Zap },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('management');
  const [unreadCommissionCount, setUnreadCommissionCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem('vyrus_user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData.role === 'management' || userData.role === 'staff') {
          setUserRole(userData.role);
          if (userData.role === 'management') {
            fetchUnreadCount();
          }
        } else {
          fetchUserRole();
        }
      } catch (e) {
        console.error('Failed to parse user data');
        fetchUserRole();
      }
    }
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      if (!token) return;
      const response = await axios.get(`${BACKEND_URL}/api/commissions/unread-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCommissionCount(response.data.count || 0);
    } catch (e) {
      // Silently fail
    }
  };

  useEffect(() => {
    if (userRole === 'management') {
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [userRole]);

  const fetchUserRole = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      if (!token) return;
      
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.role) {
        setUserRole(response.data.role);
        localStorage.setItem('vyrus_user', JSON.stringify(response.data));
      }
    } catch (e) {
      console.log('Could not fetch user role, using default');
    }
  };

  const navigation = userRole === 'management' ? managementNavigation : baseNavigation;

  const handleLogout = () => {
    localStorage.removeItem('vyrus_token');
    localStorage.removeItem('vyrus_user');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background flex flex-col overflow-x-hidden">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-card/95 backdrop-blur-lg border-b border-border z-50 h-14 flex items-center justify-between px-4">
        <h1 className="text-xl font-bold text-primary font-heading tracking-tight">
          VYRUS HUB
        </h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-muted-foreground"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Menu */}
          <div className="absolute top-14 right-0 bottom-0 w-72 bg-card border-l border-border flex flex-col">
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                const showBadge = item.name === 'Commissions' && unreadCommissionCount > 0;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all
                      ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}
                    `}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                    {showBadge && (
                      <span className="ml-auto bg-pink-500 text-white text-xs rounded-full px-2 py-0.5">
                        {unreadCommissionCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
            <div className="flex-shrink-0 border-t border-border p-4 pb-24">
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start text-muted-foreground hover:text-destructive"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden md:block md:fixed md:inset-y-0 md:left-0 md:w-64 md:z-50">
        <div className="flex flex-col h-full border-r border-border bg-card">
          <div className="flex items-center flex-shrink-0 px-6 py-8">
            <h1 className="text-3xl font-bold text-primary font-heading tracking-tight">
              VYRUS HUB
            </h1>
          </div>
          <nav className="flex-1 px-4 space-y-2 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              const showBadge = item.name === 'Commissions' && unreadCommissionCount > 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`
                    group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all
                    ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent/10 hover:text-accent'}
                  `}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                  {showBadge && (
                    <span className="ml-auto bg-pink-500 text-white text-xs rounded-full px-2 py-0.5">
                      {unreadCommissionCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="flex-shrink-0 flex border-t border-border p-4">
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-button"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:pl-64 h-screen overflow-hidden">
        <main className="h-full pt-14 pb-36 md:pt-0 md:pb-0 overflow-y-auto overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 pb-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Only 5 items */}
      <div className="md:hidden fixed bottom-14 left-0 right-0 bg-card/95 backdrop-blur-lg border-t border-border z-50 h-16 flex items-center justify-around px-2">
        {mobileNavigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors ${
                isActive ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <Icon className="h-5 w-5 mb-1" />
              <span className="text-[10px] font-medium">{item.name}</span>
            </Link>
          );
        })}
        {/* Menu button in bottom nav */}
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="flex flex-col items-center justify-center py-2 px-3 rounded-lg text-muted-foreground"
        >
          <Menu className="h-5 w-5 mb-1" />
          <span className="text-[10px] font-medium">More</span>
        </button>
      </div>
    </div>
  );
}
